/**
 * WelcomeTourCard — the first-login invitation to the brief welcome tour.
 * Shown by useAutoTutorial; starting the tour hands off to the spotlight overlay.
 */
import { useEffect, useState } from "react";
import { Volume2 } from "lucide-react";
import { Dialog } from "../Dialog";
import Button from "../Button";
import { useTutorial } from "../../hooks/useTutorial";
import { TUTORIAL_STORAGE_KEYS } from "./types";
import { pushPromptOverlay } from "../../pwa/prompt-overlay";

interface WelcomeTourCardProps {
  open: boolean;
  onClose: () => void;
  onDismissForever: () => void;
}

export function WelcomeTourCard({ open, onClose, onDismissForever }: WelcomeTourCardProps) {
  const { startBriefTour } = useTutorial();
  const [narrationOn, setNarrationOn] = useState(
    () => localStorage.getItem(TUTORIAL_STORAGE_KEYS.narrationEnabled) !== "false"
  );

  // While the welcome dialog's scrim is up, register it so deferring nudges
  // (feature announcements) don't stack underneath it.
  useEffect(() => {
    if (!open) return;
    return pushPromptOverlay();
  }, [open]);

  if (!open) return null;

  const handleStart = () => {
    localStorage.setItem(
      TUTORIAL_STORAGE_KEYS.narrationEnabled,
      narrationOn ? "true" : "false"
    );
    onClose();
    startBriefTour();
  };

  return (
    <Dialog
      variant="center"
      size="md"
      zIndexClassName="z-[90]"
      onClose={onClose}
      closeAriaLabel="Close welcome"
      title="Welcome to Autumn!"
      subtitle="Let me show you around — it takes 2 minutes."
      footer={
        <div className="flex items-center justify-between gap-4">
          <button
            onClick={onDismissForever}
            className="text-footnote text-content-tertiary hover:text-content-secondary transition-colors"
          >
            Don't show this again
          </button>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose}>
              Maybe later
            </Button>
            <Button onClick={handleStart}>Start the tour</Button>
          </div>
        </div>
      }
    >
      <p className="text-callout text-content-secondary leading-relaxed">
        A quick once-around of the essentials, so you know exactly where
        everything lives. You can replay it anytime from Take a Tour in the
        sidebar.
      </p>
      <label className="mt-4 flex items-center gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={narrationOn}
          onChange={(e) => setNarrationOn(e.target.checked)}
          className="w-4 h-4 accent-[var(--d-accent)]"
        />
        <span className="flex items-center gap-2 text-callout text-content-primary">
          <Volume2 className="w-4 h-4" />
          Talk me through it
        </span>
      </label>
    </Dialog>
  );
}
